import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Select, { MultiValue } from "react-select";
import { toast } from "react-toastify";
import { createLead, CreateLeadPayload } from "../../../api/leads";
import { Contact } from "../../../api/lead";
import { fetchAllLeadStagesActive } from "../../../api/leadstage";
import { fetchAllLeadSourcesActive } from "../../../api/leadsource";
import { fetchAllLeadTypesActive } from "../../../api/leadtype";
import { fetchAllLeadIndustriesActive } from "../../../api/leadindustry";
import { fetchAllProductTypesActive } from "../../../api/producttype";
import { fetchAllCustomerPotentialsActive } from "../../../api/customertype";
import { fetchAllProductsActive } from "../../../api/product";
import { fetchUsersByClientId } from "../../../api/todo";
import { fetchAllLeadGivenByActive } from "../../../api/leadgivenby";
import { useSelector } from "react-redux";
import { RootState } from "../../../core/data/redux/store";


interface ConvertToLeadModalProps {
  show: boolean;
  onHide: () => void;
  selectedContacts: Contact[];
  onSuccess: () => void;
}

const ConvertToLeadModal: React.FC<ConvertToLeadModalProps> = ({
  show,
  onHide,
  selectedContacts,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [leadStageOptions, setLeadStageOptions] = useState<{ label: string; value: string }[]>([]);
  const [leadSourceOptions, setLeadSourceOptions] = useState<{ label: string; value: string }[]>([]);
  const [leadTypeOptions, setLeadTypeOptions] = useState<{ label: string; value: string }[]>([]);
  const [leadIndustryOptions, setLeadIndustryOptions] = useState<{ label: string; value: string }[]>([]);
  const [leadUserOptions, setLeadUserOptions] = useState<{ label: string; value: string }[]>([]);
  const [productOptions, setProductOptions] = useState<{ label: string; value: string }[]>([]);
  const [productTypeOptions, setProductTypeOptions] = useState<{ label: string; value: string }[]>([]);
  const [customerPotentialOptions, setCustomerPotentialOptions] = useState<{ label: string; value: string }[]>([]);
  const [leadGivenByOptions, setLeadGivenByOptions] = useState<{ label: string; value: string }[]>([]);
  const [selectedContributors, setSelectedContributors] = useState<
    { label: string; value: string }[]
  >([]);
  const [selectedEditProduct, setSelectedEditProduct] = useState<{
    label: string;
    value: string;
    data: {
      no_logins: number | null;
      rate_per_login: number | null;
      product_price: number;
    };
  } | null>(null);
  
  const currencyOptions = [
      { value: "INR", label: "INR" },
      { value: "Dollar", label: "Dollar" },
      { value: "Euro", label: "Euro" },
      { value: "Pound", label: "Pound" },
  ];


  const clientId = useSelector((state: RootState) => state.auth.client_id);


  const [formData, setFormData] = useState({
    lead_status: "Not_Contacted",
    leadstage_id: "",
    leadsource_id: "",
    leadtype_id: "",
    leadindustry_id: "",
    user_id: "", // Assigned to
    product_id: "",
    product_type_id: "",
    customerPotential_id: "",
    lead_description: "",
    leadgivenby_id: "",
    migrating_from: "",
    lead_value: "",
    Currency: "INR",
    no_logins: 0,
    rate_per_login: 0,
  });

  useEffect(() => {
    if (show) {
      loadDropdowns();
    }
  }, [show]);

    const loadDropdowns = async () => {
    try {
      const [stages, sources, types, industries, products, pTypes, potentials, usersData, givenBy] = await Promise.all([
        fetchAllLeadStagesActive(),
        fetchAllLeadSourcesActive(),
        fetchAllLeadTypesActive(),
        fetchAllLeadIndustriesActive(),
        fetchAllProductsActive(),
        fetchAllProductTypesActive(),
        fetchAllCustomerPotentialsActive(),
        fetchUsersByClientId(clientId || ""),
        fetchAllLeadGivenByActive(),
      ]);

      setLeadStageOptions(stages.map((s: any) => ({ label: s.LeadStage_name, value: s.LeadStage_id })));
      setLeadSourceOptions(sources.map((s: any) => ({ label: s.LeadSource_name, value: s.LeadSource_id })));
      setLeadTypeOptions(types.map((t: any) => ({ label: t.leadtype_name, value: t.leadtype_id })));
      setLeadIndustryOptions(industries.map((i: any) => ({ label: i.LeadIndustry_name, value: i.LeadIndustry_id })));
      setProductOptions(products.map((p: any) => ({ 
          label: `${p.product_name} (₹${p.product_price})`,
          value: p.product_id,
          data: {
              no_logins: p.no_logins ?? null,
              rate_per_login: p.rate_per_login ?? null,
              product_price: p.product_price ?? 0,
          },
      })));
      setProductTypeOptions(pTypes.map((p: any) => ({ label: p.product_name, value: p.product_type_id })));
      setCustomerPotentialOptions(potentials.map((p: any) => ({ label: p.customerPotential_name, value: p.customerPotential_id })));
      setLeadUserOptions(usersData.map((u: any) => ({ label: u.name, value: u.id })));
      setLeadGivenByOptions(givenBy.map((g: any) => ({ label: g.LeadGivenBy_name, value: g.leadgivenby_id })));

    } catch (error) {
      console.error("Failed to load dropdowns", error);
      toast.error("Failed to load form options");
    }
  };

  const handleSubmit = async () => {
    if (selectedContacts.length === 0) {
        toast.error("No contacts selected");
        return;
    }
    


    setLoading(true);
    let successCount = 0;
    let failCount = 0;

    for (const contact of selectedContacts) {
        try {
            // Map Contact to Lead Payload
            // Assuming we take the first branch if available for branch_id, or null
            const branchId = contact.branch && contact.branch.length > 0 ? contact.branch[0].branch_id : undefined;

            const payload: CreateLeadPayload = {
                contact_id: contact.contact_id,
                contact_name: contact.contact_person_name,
                company_phone: contact.company_phone,
                contact_Company_name: contact.company_name,
                contact_mail: contact.company_mail,
                // contact_phone: contact.contact_phone ??? Not in Contact interface explicitly but might be needed
                
                website_url: contact.website_url || undefined,
                
                // Form Data
                lead_status: formData.lead_status,
                leadstage_id: formData.leadstage_id,
                leadsource_id: formData.leadsource_id,
                leadtype_id: formData.leadtype_id,
                leadindustry_id: formData.leadindustry_id,
                leadowner_id: formData.user_id, // Assign to selected user
                product_id: formData.product_id,
                product_type_id: formData.product_type_id,
                customerPotential_id: formData.customerPotential_id,
                lead_description: formData.lead_description,
                branch_id: branchId,
                
                leadgivenby_id: formData.leadgivenby_id,
                migrating_from: formData.migrating_from,
                
                no_logins: formData.no_logins, // Added
                rate_per_login: formData.rate_per_login, // Added

                // Defaults / User Input
                Currency: formData.Currency, 
                lead_value: formData.lead_value
            };

            await createLead(payload);
            successCount++;
        } catch (error) {
            console.error(`Failed to convert contact ${contact.company_name}`, error);
            failCount++;
        }
    }

    setLoading(false);
    if (successCount > 0) {
        toast.success(`Successfully converted ${successCount} contacts to leads.`);
        if (failCount > 0) {
            toast.warning(`Failed to convert ${failCount} contacts.`);
        }
        onSuccess();
        onHide();
    } else {
        toast.error("Failed to convert contacts to leads.");
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <div className="modal-header border-0 m-0 justify-content-between">
        <h5 className="modal-title">Convert to Lead</h5>
        <button
          className="btn-close"
          aria-label="Close"
          onClick={onHide}
        >
          <i className="ti ti-x" />
        </button>
      </div>
      <div className="modal-body">
        <div className="alert alert-info">
            You are about to convert <strong>{selectedContacts.length}</strong> contacts into leads. 
            Please select the common details for these new leads.
        </div>
        <form>
            <div className="row">
                {/* Assign To */}
                <div className="col-md-6 mb-3">
                    <label className="form-label">Assign To</label>
                    <Select
                        options={leadUserOptions}
                        onChange={(opt) => setFormData({ ...formData, user_id: opt?.value || "" })}
                        placeholder="Select User"
                    />
                </div>

                {/* Lead Source */}
                <div className="col-md-6 mb-3">
                     <label className="form-label">Lead Source <span className="text-danger">*</span></label>
                     <div className="input-group">
                        <span className="input-group-text">
                           <i className="ti ti-database" />
                        </span>
                        <div className="flex-grow-1">
                             <Select
                                options={leadSourceOptions}
                                onChange={(opt) => setFormData({ ...formData, leadsource_id: opt?.value || "" })}
                                placeholder="Select Source"
                            />
                        </div>
                    </div>
                </div>

                {/* Lead Given By */}
                <div className="col-md-6 mb-3">
                     <label className="form-label">Lead Given By <span className="text-danger">*</span></label>
                     <div className="input-group">
                        <span className="input-group-text">
                           <i className="ti ti-user" />
                        </span>
                        <div className="flex-grow-1">
                             <Select
                                options={leadGivenByOptions}
                                onChange={(opt) => setFormData({ ...formData, leadgivenby_id: opt?.value || "" })}
                                placeholder="Select Lead Given By"
                            />
                        </div>
                    </div>
                </div>

                {/* Product */}
                 <div className="col-md-6 mb-3">
                     <label className="form-label">Product <span className="text-danger">*</span></label>
                     <div className="input-group">
                        <span className="input-group-text">
                           <i className="bi bi-box-seam" />
                        </span>
                        <div className="flex-grow-1">
                             <Select
                                options={productOptions}
                                onChange={(opt) => {
                                    setFormData({ ...formData, product_id: opt?.value || "" });
                                    if (opt && "data" in opt) {
                                         // @ts-ignore
                                        setSelectedEditProduct(opt);
                                    } else {
                                        setSelectedEditProduct(null);
                                    }
                                }}
                                placeholder="Select Product"
                            />
                        </div>
                    </div>
                </div>

                {/* Lead Status */}
                <div className="col-md-6 mb-3">
                    <label className="form-label">Lead Status <span className="text-danger">*</span></label>
                     <div className="input-group">
                        <span className="input-group-text">
                           <i className="ti ti-check" />
                        </span>
                        <div className="flex-grow-1">
                            <select 
                                className="form-select"
                                value={formData.lead_status}
                                onChange={(e) => setFormData({ ...formData, lead_status: e.target.value })}
                            >
                                <option value="Not_Contacted">Not Contacted</option>
                                <option value="Contacted">Contacted</option>
                                <option value="Closed">Closed</option>
                                <option value="Lost">Lost</option>
                            </select>
                        </div>
                    </div>
                </div>
                
                {/* Lead Stage */}
                <div className="col-md-6 mb-3">
                    <label className="form-label">Lead Stage</label>
                    <div className="input-group">
                        <span className="input-group-text">
                           <i className="bi bi-boxes" />
                        </span>
                        <div className="flex-grow-1">
                             <Select
                                options={leadStageOptions}
                                onChange={(opt) => setFormData({ ...formData, leadstage_id: opt?.value || "" })}
                                placeholder="Select Stage"
                            />
                        </div>
                    </div>
                </div>

                {/* Lead Type */}
                <div className="col-md-6 mb-3">
                     <label className="form-label">Lead Type</label>
                     <div className="input-group">
                        <span className="input-group-text">
                           <i className="ti ti-category" />
                        </span>
                        <div className="flex-grow-1">
                             <Select
                                options={leadTypeOptions}
                                onChange={(opt) => setFormData({ ...formData, leadtype_id: opt?.value || "" })}
                                placeholder="Select Type"
                            />
                        </div>
                    </div>
                </div>

                {/* Industry / Business Type */}
                <div className="col-md-6 mb-3">
                     <label className="form-label">Lead Business Type</label>
                     <div className="input-group">
                        <span className="input-group-text">
                           <i className="ti ti-file-invoice" />
                        </span>
                        <div className="flex-grow-1">
                             <Select
                                options={leadIndustryOptions}
                                onChange={(opt) => setFormData({ ...formData, leadindustry_id: opt?.value || "" })}
                                placeholder="Select Industry"
                            />
                        </div>
                    </div>
                </div>

                 

                {/* Conditional Logins & Rate */}
                {selectedEditProduct && (
                  <>
                    <div className="col-md-6 col-sm-12 mb-3">
                      <label className="form-label">No. of Logins</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className="form-control"
                        value={formData.no_logins === undefined ? "" : formData.no_logins}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "") {
                            setFormData((prev) => ({ ...prev, no_logins: 0 }));
                            return;
                          }
                          const logins = parseInt(value, 10);
                          if (!isNaN(logins)) {
                            setFormData((prev) => ({
                              ...prev,
                              no_logins: logins,
                              rate_per_login: selectedEditProduct?.data?.product_price || 0,
                            }));
                          }
                        }}
                      />
                    </div>
                    <div className="col-md-6 col-sm-12 mb-3">
                      <label className="form-label">Rate per Login</label>
                      <input
                        type="text"
                        className="form-control"
                        value={`₹${selectedEditProduct?.data?.product_price || 0}`}
                        readOnly
                      />
                    </div>
                  </>
                )}

                 {/* Product Type */}
                 <div className="col-md-6 mb-3">
                     <label className="form-label">Product Type</label>
                     <div className="input-group">
                        <span className="input-group-text">
                           <i className="ti ti-box" />
                        </span>
                        <div className="flex-grow-1">
                             <Select
                                options={productTypeOptions}
                                onChange={(opt) => setFormData({ ...formData, product_type_id: opt?.value || "" })}
                                placeholder="Select Product Type"
                            />
                        </div>
                    </div>
                </div>

                 {/* Customer Potential */}
                 <div className="col-md-6 mb-3">
                     <label className="form-label">Customer Potential</label>
                     <div className="input-group">
                        <span className="input-group-text">
                           <i className="ti ti-trending-up" />
                        </span>
                        <div className="flex-grow-1">
                             <Select
                                options={customerPotentialOptions}
                                onChange={(opt) => setFormData({ ...formData, customerPotential_id: opt?.value || "" })}
                                placeholder="Select Potential"
                            />
                        </div>
                    </div>
                </div>

                {/* Migrating From */}
                <div className="col-md-6 mb-3">
                     <label className="form-label">Migrating From</label>
                     <div className="input-group">
                        <span className="input-group-text">
                           <i className="ti ti-transfer" />
                        </span>
                        <div className="flex-grow-1">
                            <input
                                type="text"
                                className="form-control"
                                value={formData.migrating_from}
                                onChange={(e) => setFormData({ ...formData, migrating_from: e.target.value })}
                                placeholder="e.g. Competitor X"
                            />
                        </div>
                    </div>
                </div>

                {/* Quoted Value */}
                <div className="col-md-6 mb-3">
                     <label className="form-label">Quoted Value</label>
                     <input
                        type="text"
                        className="form-control"
                        value={formData.lead_value}
                        onChange={(e) => setFormData({ ...formData, lead_value: e.target.value })}
                        placeholder="Value"
                    />
                </div>

                {/* Currency */}
                <div className="col-md-6 mb-3">
                     <label className="form-label">Currency</label>
                     <div className="input-group">
                        <span className="input-group-text">
                           <i className="ti ti-currency-dollar" />
                        </span>
                        <div className="flex-grow-1">
                             <Select
                                options={currencyOptions}
                                value={currencyOptions.find(c => c.value === formData.Currency)}
                                onChange={(opt) => setFormData({ ...formData, Currency: opt?.value || "INR" })}
                                placeholder="Select Currency"
                            />
                        </div>
                    </div>
                </div>

                

                 <div className="col-md-12 mb-3">
                    <label className="form-label">Description / Remarks</label>
                    <textarea 
                        className="form-control" 
                        rows={3}
                        value={formData.lead_description}
                        onChange={(e) => setFormData({ ...formData, lead_description: e.target.value })}
                    />
                </div>

            </div>
        </form>
      </div>
      <div className="modal-footer">
        <button
          className="btn btn-light"
          onClick={onHide}
        >
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? "Converting..." : "Convert"}
        </button>
      </div>
    </Modal>
  );
};

export default ConvertToLeadModal;
